import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Box, MessageCircle, Trash2 } from 'lucide-react'
import {
  getQuoteItemCount,
  getQuoteMessageCount,
  getQuoteOrderValue,
  useQuoteApi,
  type QuoteListRow,
} from 'src/api/db/apiQuote'
import { QuoteOrderValue } from 'src/shared/components/QuoteOrderValue'
import { buildAdminOrderPath } from 'src/routes/paths'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Image } from 'src/shared/components/ui/image'
import { cn } from 'src/shared/components/ui/utils'
import { useToast } from 'src/shared/hooks/use-toast'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { getQuotePreviewThumbnails } from 'src/shared/utils/quoteItemThumbnail'

export const PipelineCard = ({
  quote,
  placeholderClassName,
}: {
  quote: QuoteListRow
  placeholderClassName?: string
}) => {
  const navigate = useNavigate()
  const { t, locale } = useLocalization()
  const { toast } = useToast()
  const { deleteQuote } = useQuoteApi({ skipListQuery: true })
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { previews } = useMemo(
    () => getQuotePreviewThumbnails(quote.quote_item, 1),
    [quote.quote_item]
  )
  const leadUrl = previews[0]?.url
  const itemCount = getQuoteItemCount(quote)
  const messageCount = getQuoteMessageCount(quote)
  const orderValue = getQuoteOrderValue(quote)
  const extraCount = Math.max(0, itemCount - 1)
  const quoteLabel =
    quote.name?.trim() || t('Untitled request', 'Namnlös förfrågan')
  const orgLabel =
    quote.organization?.name || t('Unassigned', 'Otilldelad')
  const createdAt = quote.created_at ? new Date(quote.created_at) : null
  const createdAtLabel =
    createdAt && !Number.isNaN(createdAt.getTime())
      ? createdAt.toLocaleString(locale === 'sv' ? 'sv-SE' : 'en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      : null

  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    window.addEventListener('pointerdown', close)
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', close)
    return () => {
      window.removeEventListener('pointerdown', close)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('keydown', close)
    }
  }, [menu])

  const handleDelete = async () => {
    try {
      await deleteQuote.mutateAsync(quote.id)
      toast({
        variant: 'success',
        title: t('Request deleted', 'Förfrågan borttagen'),
      })
      setConfirmOpen(false)
    } catch (error) {
      console.error('Failed to delete quote:', error)
      toast({
        variant: 'destructive',
        title: t('Could not delete request', 'Kunde inte ta bort förfrågan'),
      })
    }
  }

  return (
    <>
      <button
        type="button"
        className="w-full rounded-xl border border-[hsl(var(--border-subtle))] bg-background px-3 py-3.5 text-left shadow-soft-sm transition-shadow hover:shadow-soft"
        onClick={() => navigate(buildAdminOrderPath(quote.public_id ?? ''))}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setMenu({ x: e.clientX, y: e.clientY })
        }}
      >
        <Row className="items-start gap-2.5">
          <div className="relative h-10 w-10 shrink-0">
            {leadUrl ? (
              <div className="h-10 w-10 overflow-hidden rounded-lg bg-muted">
                <Image
                  src={leadUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </div>
            ) : (
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold',
                  placeholderClassName ?? 'bg-muted text-muted-foreground'
                )}
              >
                {orgLabel.charAt(0).toUpperCase()}
              </div>
            )}
            {extraCount > 0 ? (
              <span className="absolute -bottom-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium text-background">
                +{extraCount}
              </span>
            ) : null}
          </div>

          <Column className="min-w-0 flex-1 gap-1">
            <Row className="items-start justify-between gap-2">
              <p className="min-w-0 truncate text-sm font-medium leading-snug">
                {quoteLabel}
              </p>
              <QuoteOrderValue amount={orderValue} size="sm" className="shrink-0" />
            </Row>
            <p className="min-w-0 truncate text-xs text-muted-foreground">
              {orgLabel}
            </p>
            <Row className="items-center justify-between gap-2">
              {createdAtLabel ? (
                <p
                  className="min-w-0 truncate text-[11px] tabular-nums text-muted-foreground"
                  title={t('Created', 'Skapad') + ' ' + createdAtLabel}
                >
                  {createdAtLabel}
                </p>
              ) : (
                <span />
              )}
              <Row className="shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Row
                  className="items-center gap-0.5"
                  title={t('Items', 'Artiklar')}
                >
                  <Box className="h-3 w-3" />
                  <span className="tabular-nums">{itemCount}</span>
                </Row>
                <Row
                  className={cn(
                    'items-center gap-0.5',
                    messageCount > 0 && 'text-violet-700'
                  )}
                  title={t('Messages', 'Meddelanden')}
                >
                  <MessageCircle className="h-3 w-3" />
                  <span className="tabular-nums">{messageCount}</span>
                </Row>
              </Row>
            </Row>
          </Column>
        </Row>
      </button>

      {menu &&
        createPortal(
          <Column
            className="fixed z-[200] min-w-[168px] gap-0.5 rounded-lg border border-border bg-background p-1 shadow-xl"
            style={{ left: menu.x, top: menu.y }}
            onPointerDown={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <button
              type="button"
              className="flex w-full items-center justify-start gap-2 rounded-md px-2.5 py-1.5 text-left text-xs text-destructive transition-colors hover:bg-destructive/10"
              onClick={() => {
                setMenu(null)
                setConfirmOpen(true)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('Delete', 'Ta bort')}
            </button>
          </Column>,
          document.body
        )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('Delete request?', 'Ta bort förfrågan?')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t(
              `This will permanently delete "${quoteLabel}" and all of its items and messages.`,
              `Detta tar bort "${quoteLabel}" och alla artiklar och meddelanden, och går inte att ångra.`
            )}
          </p>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={deleteQuote.isPending}
            >
              {t('Cancel', 'Avbryt')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={deleteQuote.isPending}
              isLoading={deleteQuote.isPending}
            >
              {t('Delete', 'Ta bort')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
